import { useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import CircularProgress from 'components/shared/CircularProgress';
import Input from 'components/shared/form/Input';
import { useCoordinatePatientsStore } from 'stores/patientFiltersStore';
import CoordinatePatientsIcon from 'svg/CoordinatePatientsIcon';
import SearchIcon from 'svg/SearchIcon';
import XIcon2 from 'svg/XIcon2';

const SearchInput = ({
  searchValue,
  onSearch,
  setSearchValue,
}: {
  searchValue: string;
  onSearch: (value: string) => void;
  setSearchValue: (value: string) => void;
}) => {
  return (
    <SearchInputWrapper>
      <SearchIconWrapper>
        <SearchIcon width={13} height={13} />
      </SearchIconWrapper>
      <StyledSearchInput
        id='coordinate-search'
        placeholder='Search'
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && onSearch(searchValue)}
      />
      {searchValue && (
        <ClearButton onClick={() => setSearchValue('')} aria-label='Clear search'>
          <XIcon2 width={12} height={12} />
        </ClearButton>
      )}
    </SearchInputWrapper>
  );
};

export const SearchButton = ({
  searchValue,
  onSearch,
  isLoading,
}: {
  searchValue: string;
  onSearch: (value: string) => void;
  isLoading: boolean;
}) => {
  return (
    <StyledSearchButton disabled={!searchValue.trim()} onClick={() => onSearch(searchValue)} fullWidth>
      <span style={{ opacity: isLoading ? 0 : 1 }}>Search</span>
      {isLoading && (
        <SpinnerContainer>
          <ExportLoadingIndicator thickness={2} />
        </SpinnerContainer>
      )}
    </StyledSearchButton>
  );
};

type CoordinateSearchProps = {
  onSearch: (search: string) => void;
  isLoading: boolean;
};

function CoordinateSearch({ onSearch, isLoading = false }: CoordinateSearchProps) {
  const { search } = useCoordinatePatientsStore();
  const [searchValue, setSearchValue] = useState(search);

  return (
    <Content>
      <HeadlineWrapper>
        <CoordinatePatientsIcon />
        <Headline>Coordinate</Headline>
      </HeadlineWrapper>
      <SubHeadline>Search for a name or ID to begin coordinating care in Olio.</SubHeadline>
      <SearchContainer>
        <SearchInput {...{ searchValue, onSearch, setSearchValue }} />
        <SearchButton {...{ searchValue, onSearch, isLoading }} />
      </SearchContainer>
    </Content>
  );
}

export default CoordinateSearch;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0 48px 24px;
  max-width: 600px;
`;

const HeadlineWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding-top: 24px;
  padding-bottom: 12px;
  gap: 12px;
`;

const Headline = styled.h1`
  font-size: 24px;
  font-weight: var(--font-weight-medium);
  color: ${({ theme }) => theme.colors.black100};
`;

const SubHeadline = styled.p`
  font-size: 16px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.black100};
  margin: 0;
  padding-top: 12px;
  padding-bottom: 12px;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
  max-width: 480px;
  margin-top: 12px;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 10px;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.black25};
  pointer-events: none;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.black25};
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.black100};
  }

  &:focus {
    outline: none;
    color: ${({ theme }) => theme.colors.black100};
  }
`;

const StyledSearchInput = styled(Input)`
  width: 440px;
  height: 40px;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.black25};
  border-radius: ${({ theme }) => theme.dimensions.borderRadius};
  padding: 8px 32px 8px 29px;
  font-size: 14px;
  line-height: 20px;

  &::placeholder {
    color: ${({ theme }) => theme.colors.black25};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.blue100};
    outline: none;
  }
`;

const StyledSearchButton = styled(Button)`
  padding: 8px 24px;
  color: ${({ theme }) => theme.colors.white};
  background-color: ${({ theme }) => theme.colors.blue100};
  opacity: ${({ disabled }) => (disabled ? 0.25 : 1)};

  &:hover {
    background-color: ${({ theme, disabled }) => (disabled ? theme.colors.blue100 : theme.colors.blueHover)};
  }
  position: relative;
`;

const SpinnerContainer = styled.div`
  position: absolute;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ExportLoadingIndicator = styled(CircularProgress)`
  width: 18px;
  height: 18px;
  color: ${({ theme }) => theme.colors.blue100};
`;
